import type { QuestionAnswer } from "@/components/ask-getabstract/utils/store";
import type { DeepReadonly } from "vue";
import type { AnimationTimer } from "@/components/ask-getabstract/utils/animationUtils";
import { createRunStreamingAnimationEndless, createRunStreamingAnimationUntilMaxReached } from "@/components/ask-getabstract/utils/animationUtils";
import type { AbortableFunction } from "@utils/asyncUtils";
import { runAbortableFunctions } from "@utils/asyncUtils";
import type { AskGetabstractAnalyticsEventVariant, AskGetabstractQuestion, AskGetabstractQuestionAnswers } from "@newgenerated/shared/schema";
import type { ApiResult } from "@utils/httpClientCore";

export async function tryInitializeQuestion(props: {
  isTrendingQuestion: boolean;
  analyticsEventVariant: AskGetabstractAnalyticsEventVariant;
  history: DeepReadonly<QuestionAnswer[]>;
  current: DeepReadonly<QuestionAnswer>;
  initQuestionAnswer: (props: AskGetabstractQuestion) => Promise<ApiResult>;
}): Promise<{ kind: "SUCCESS" | "ERROR" }> {
  const blacklistQuestions: string[] = [...props.history.map((e) => e.question), props.current.question];
  const relatedQuestionStep = props.history.length;
  const result = await props.initQuestionAnswer({
    question: props.current.question,
    questionUuid: props.current.questionUuid,
    trendingQuestion: props.isTrendingQuestion,
    blacklistQuestions,
    previousQuestionUuid: props.history.at(-1)?.questionUuid ?? null,
    relatedQuestionStep,
    analyticsEventVariant: props.analyticsEventVariant,
  });
  if (result.kind === "success") {
    return {
      kind: "SUCCESS",
    };
  } else {
    return {
      kind: "ERROR",
    };
  }
}

export type PollingResult =
  | {
      kind: "SUCCESS";
      value: AskGetabstractQuestionAnswers;
    }
  | { kind: "ERROR" }
  | { kind: "CANCELLED" };

export function createRunPolling(
  props: {
    questionUuid: string;
  },
  actions: {
    getQuestionAnswers: (questionUuid: string) => Promise<ApiResult<AskGetabstractQuestionAnswers>>;
    runUntil: (questionAnswers: AskGetabstractQuestionAnswers) => boolean;
    updateWhileProcessing: (value: AskGetabstractQuestionAnswers) => Promise<void>;
  },
): AbortableFunction<PollingResult> {
  return async (signal: AbortSignal) => {
    while (true) {
      if (signal.aborted) {
        return { kind: "CANCELLED" };
      }
      const questionAnswers = await actions.getQuestionAnswers(props.questionUuid);
      if (questionAnswers.kind === "success") {
        await actions.updateWhileProcessing(questionAnswers.data);
        if (actions.runUntil(questionAnswers.data)) {
          return { kind: "SUCCESS", value: questionAnswers.data };
        }
      } else {
        return { kind: "ERROR" };
      }
    }
  };
}

export function createProcessQuestion(
  props: {
    current: DeepReadonly<QuestionAnswer>;
    history: DeepReadonly<QuestionAnswer[]>;
    isTrendingQuestion: boolean;
    analyticsEventVariant: AskGetabstractAnalyticsEventVariant;
    streamingTokensPerSec: number;
    streamingSpeedUpFactor: number;
  },
  actions: {
    initQuestionAnswer: (props: AskGetabstractQuestion) => Promise<ApiResult>;
    getQuestionAnswers: (questionUuid: string) => Promise<ApiResult<AskGetabstractQuestionAnswers>>;
    updateWhileProcessing: (value: AskGetabstractQuestionAnswers) => Promise<void>;
    startTimer: () => AnimationTimer;
    delay: () => Promise<void>;
    updateTokenCount: (tokenCount: number) => void;
  },
): AbortableFunction<PollingResult> {
  return async (signal: AbortSignal) => {
    const initResult = await tryInitializeQuestion({
      isTrendingQuestion: props.isTrendingQuestion,
      analyticsEventVariant: props.analyticsEventVariant,
      history: props.history,
      current: props.current,
      initQuestionAnswer: actions.initQuestionAnswer,
    });

    if (initResult.kind === "ERROR") {
      return { kind: "ERROR" };
    }

    const runPollingUntilFirstWord = createRunPolling(
      {
        questionUuid: props.current.questionUuid,
      },
      {
        getQuestionAnswers: actions.getQuestionAnswers,
        runUntil: (questionAnswers) => {
          return questionAnswers.current.status !== "PROCESSING" || questionAnswers.current.answer.length > 0;
        },
        updateWhileProcessing: (value) => {
          return actions.updateWhileProcessing(value);
        },
      },
    );

    const resultUntilFristWord = await runAbortableFunctions([runPollingUntilFirstWord], () => signal.aborted, actions.delay);

    if (resultUntilFristWord.kind !== "SUCCESS" || resultUntilFristWord.value.current.status !== "PROCESSING") {
      return resultUntilFristWord;
    }

    let answer = "";
    let alreadyAnimatedTokens = 0;
    const runPollingUntilDone = createRunPolling(
      {
        questionUuid: props.current.questionUuid,
      },
      {
        getQuestionAnswers: actions.getQuestionAnswers,
        runUntil: (questionAnswers) => questionAnswers.current.status !== "PROCESSING",
        updateWhileProcessing: (questionAnswers) => {
          answer = questionAnswers.current.answer;
          return actions.updateWhileProcessing(questionAnswers);
        },
      },
    );

    const runStreamingAnimation = createRunStreamingAnimationEndless(
      {
        streamingTokensPerSec: props.streamingTokensPerSec,
      },
      {
        startTimer: actions.startTimer,
        delay: actions.delay,
        getMaxTokenAvailable: () => answer.split(" ").length,
        updateTokenCount: (tokenCount) => {
          alreadyAnimatedTokens = tokenCount;
          return actions.updateTokenCount(tokenCount);
        },
      },
    );

    const pollingUntilDoneResult = await runAbortableFunctions([runPollingUntilDone, runStreamingAnimation], () => signal.aborted, actions.delay);

    if (pollingUntilDoneResult.kind !== "SUCCESS" || pollingUntilDoneResult.value.current.status !== "FINISHED") {
      return pollingUntilDoneResult;
    }

    const runStreamingAnimationUntilDone = createRunStreamingAnimationUntilMaxReached(
      {
        streamingTokensPerSec: props.streamingTokensPerSec,
        streamingSpeedUpFactor: props.streamingSpeedUpFactor,
        alreadyAnimatedTokens: alreadyAnimatedTokens,
      },
      {
        startTimer: actions.startTimer,
        delay: actions.delay,
        getMaxTokenAvailable: () => answer.split(" ").length,
        updateTokenCount: actions.updateTokenCount,
      },
    );

    const streamingResult = await runAbortableFunctions([runStreamingAnimationUntilDone], () => signal.aborted, actions.delay);

    if (streamingResult.kind === "CANCELLED") {
      return { kind: "CANCELLED" };
    } else {
      return pollingUntilDoneResult;
    }
  };
}
